import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import OrganSystemView from "../components/OrganSystemView";
import UserHeader from "../components/UserHeader/UserHeader";
import BottomNav from "../components/BottomNav/BottomNav";
import { useTabSwipe } from "../hooks/useTabSwipe";
import TestDeletePopup from "../components/TestDeletePopup";
import MobileProtocol from "../components/Recommendations/MobileProtocol";
import ChatDrawer from "../components/ChatDrawer/ChatDrawer";
import ElisLockedPanel from "../components/ChatDrawer/ElisLockedPanel";
import TestDetailsSection from "../components/TestDetailsSection/TestDetailsSection";
import FocusDashboard from "../components/FocusDashboard/FocusDashboard";
import "../styles/Home.css";

function MobileHome({
    user,
    profileData,
    demo = false,
    demoMode = false,
    initialTab = 'dashboard',
    systemScoresArray,
    isLoadingScores,
    selectedTest,
    handleClearSelectedTest,
    handleTestClick,
    bloodTestFiles,
    loadingFiles,
    getFxRange,
    sex,
    selectedFileIndex,
    previousScoresMap,
    selectedSystem,
    selectedFile,
    selectSystem,
    forceSelectSystem,
    recommendations,
    recommendationMode,
    setRecommendationMode,
    loadingRecommendations,
    selectedAlternatives,
    handleSelectAlternative,
    handleGetRecommendations,
    handleGetAllDefaults,
    highlightedRecommendations,
    savedProtocol,
    protocolLocked,
    effectiveLocked,
    hasActiveDoctor,
    lockedByName,
    lockLoading,
    saveProtocol,
    toggleLock,
    canRegenerate,
    refreshUserFiles,
    filteredTests,
    testsForExport,
    handleFileInput,
    mergeDraw,
    uploadingState,
    setUploadingState,
    newTest,
    setNewTest,
    createNewTest,
    editTests,
    updateEditTest,
    normalizeTestName,
    userFiles,
    setUserFiles,
    selectFile,
    patientId,
    patientName,
    readOnly,
    doctorViewMode,
    showElisTab,
    elisLocked,
    elisTrialUsed,
    assessmentSummary,
    prevAssessmentSummary,
    userFocuses,
    onElisOpen,
}) {
    const greeting = (() => {
        const hours = new Date().getHours();
        const timeGreeting = hours < 12 ? "Good morning" : hours < 17 ? "Good afternoon" : "Good evening";
        const name = (profileData?.givenName || profileData?.familyName)
            ? [profileData.givenName, profileData.familyName].filter(Boolean).join(' ')
            : (user?.username ? user.username.charAt(0).toUpperCase() + user.username.slice(1) : "");
        return `${timeGreeting}, ${name}`;
    })();

    // Build dynamic tab arrays based on feature gating
    const TAB_NAME = showElisTab
        ? ['dashboard', 'protocol', 'elis']
        : ['dashboard', 'protocol'];
    const TAB_INDEX = {};
    TAB_NAME.forEach((name, i) => { TAB_INDEX[name] = i; });

    const [searchParams, setSearchParams] = useSearchParams();
    const [mobileTab, setMobileTab] = useState(initialTab);
    // Demo: track which tabs have been visited so each chip stops pulsing once seen.
    const [visitedTabs, setVisitedTabs] = useState(() => new Set([initialTab]));
    const [showTests, setShowTests] = useState(() => searchParams.get('showTests') === 'true');
    const [scrollToSystem, setScrollToSystem] = useState(null);
    const [deletePopup, setDeletePopup] = useState(false);
    const sliderRef = useRef(null);
    const dashboardPanelRef = useRef(null);
    const protocolPanelRef = useRef(null);
    const elisPanelRef = useRef(null);
    const testsScrollRef = useRef(null);
    const pagesRef = useRef(null);
    const viewportRef = useRef(null);

    const panelRefs = { dashboard: dashboardPanelRef, protocol: protocolPanelRef, elis: elisPanelRef };

    // Hook callback: just update React state when swipe/animation settles
    const handleTabSwipeComplete = useCallback((tabIndex) => {
        setMobileTab(TAB_NAME[tabIndex]);
    }, [TAB_NAME]);

    // Finger-tracking horizontal swipe between panels
    const { animateToTab } = useTabSwipe({
        sliderRef,
        containerRef: viewportRef,
        activeTab: TAB_INDEX[mobileTab],
        onTabChange: handleTabSwipeComplete,
        tabCount: TAB_NAME.length,
        enabled: !selectedSystem,
    });

    // Read ?system= on mount when coming from redirect
    useEffect(() => {
        const shouldShowTests = searchParams.get('showTests') === 'true';
        const systemParam = searchParams.get('system');

        if (shouldShowTests) {
            setShowTests(true);
        }
        if (systemParam && showTests) {
            setScrollToSystem(systemParam);
        }

        if (systemParam) {
            setShowTests(true);
            if (selectedSystem !== systemParam) {
                forceSelectSystem(systemParam);
            }
        }

        // Clean up query params after consuming them.
        if (searchParams.has('showTests') || searchParams.has('system')) {
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, selectedSystem, forceSelectSystem, setSearchParams]);

    // Clear "jump back" memory on first user touch-scroll (touchmove doesn't fire for programmatic scrolls)
    useEffect(() => {
        if (!showTests || !scrollToSystem) return;
        const scrollEl = testsScrollRef.current;
        if (!scrollEl) return;

        const clear = () => setScrollToSystem(null);
        scrollEl.addEventListener('touchmove', clear, { once: true, passive: true });
        return () => scrollEl.removeEventListener('touchmove', clear);
    }, [showTests, scrollToSystem]);

    // When clicking "View Tests" pill on organ circle, open inline tests.
    // Capture the focused system for scroll-to, then clear the filter so all tests show.
    const handleTestPillClick = useCallback(() => {
        setScrollToSystem(selectedSystem);
        selectSystem(null); // clear organ system filter — mobile scrolls instead of filtering
        setShowTests(true);
    }, [selectedSystem, selectSystem]);

    // Switch to protocol tab from organ circle modal
    const handleProtocolClick = useCallback(() => {
        selectSystem(null);
        setShowTests(false);
        setScrollToSystem(null);
        protocolPanelRef.current?.scrollTo({ top: 0, behavior: 'instant' });
        animateToTab(1);
    }, [selectSystem, animateToTab]);

    // Up chevron: if came from an organ circle, slide back then reopen the focus modal;
    // otherwise first tap scrolls tests to top, second tap returns to dashboard.
    const handleUpChevron = useCallback(() => {
        if (scrollToSystem) {
            const systemToRestore = scrollToSystem;
            setShowTests(false);
            setScrollToSystem(null);
            // Wait for the slide transition to finish, then re-select the system
            const el = pagesRef.current;
            if (el) {
                const timerId = setTimeout(() => {
                    el.removeEventListener('transitionend', onEnd);
                    selectSystem(systemToRestore);
                }, 400);
                const onEnd = (e) => {
                    if (e.propertyName === 'transform') {
                        clearTimeout(timerId);
                        el.removeEventListener('transitionend', onEnd);
                        selectSystem(systemToRestore);
                    }
                };
                el.addEventListener('transitionend', onEnd);
            }
            return;
        }
        const scrollEl = testsScrollRef.current;
        if (scrollEl && scrollEl.scrollTop > 10) {
            scrollEl.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setShowTests(false);
            dashboardPanelRef.current?.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [scrollToSystem, selectSystem]);

    // Demo: record the active tab as visited so its pulse chip clears once seen.
    useEffect(() => {
        setVisitedTabs(prev => (prev.has(mobileTab) ? prev : new Set(prev).add(mobileTab)));
    }, [mobileTab]);

    // Demo: when the Elis tab opens, play the chat intro (idempotent — safe to repeat).
    useEffect(() => {
        if (mobileTab === 'elis') onElisOpen?.();
    }, [mobileTab, onElisOpen]);

    // Make dashboard content inert (no tab/click) when a focus modal is open
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        if (selectedSystem) {
            el.setAttribute('inert', '');
        } else {
            el.removeAttribute('inert');
        }
    }, [selectedSystem]);

    // PrimeMaxing logo / dashboard button: go straight to dashboard top (instant, no animation)
    const handleGoToDashboard = useCallback(() => {
        setShowTests(false);
        setScrollToSystem(null);
        setMobileTab('dashboard');
        dashboardPanelRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    // BottomNav tab change: close tests, animate to tab, scroll target to top
    const handleTabChange = useCallback((tab) => {
        setShowTests(false);
        setScrollToSystem(null);
        const targetIndex = TAB_INDEX[tab];
        const panelRef = panelRefs[tab];
        panelRef?.current?.scrollTo({ top: 0, behavior: 'instant' });
        animateToTab(targetIndex);
    }, [animateToTab, TAB_INDEX]);

    return (
        <div className="homeTemp mobile">
            {!doctorViewMode && !readOnly && (
                <UserHeader demoMode={demoMode} onLogoClick={handleGoToDashboard}>
                    {showTests && (
                        <button className="up-chevron-btn" onClick={handleUpChevron}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 15l-6-6-6 6" />
                            </svg>
                        </button>
                    )}
                </UserHeader>
            )}
            <div className="mobile-home-viewport" ref={viewportRef}>
                <div className={`tab-slider${showElisTab ? ' three-tabs' : ''}`} ref={sliderRef}>
                    <div className="tab-panel panel-left">
                        <div ref={pagesRef} className={`vertical-pages ${showTests ? 'show-tests' : ''}`}>
                            <div
                                className="vpage-dashboard"
                                ref={dashboardPanelRef}
                                style={selectedSystem ? { overflowY: 'hidden' } : undefined}
                            >
                                <div className="mobile-content">
                                    <div className="dashboard-section">
                                        <p className="dashboard-greeting">{greeting}</p>
                                        <div className="osv-container">
                                            <div className="osv-card">
                                                <OrganSystemView
                                                    systemScoresArray={systemScoresArray}
                                                    isLoadingScores={isLoadingScores}
                                                    loadingFiles={loadingFiles}
                                                    bloodTestFiles={bloodTestFiles}
                                                    getFxRange={getFxRange}
                                                    sex={sex}
                                                    selectedFileIndex={selectedFileIndex}
                                                    previousScores={previousScoresMap}
                                                    onTestPillClick={handleTestPillClick}
                                                    onProtocolClick={handleProtocolClick}
                                                    onUploadClick={handleFileInput}
                                                    onManualEntry={() => {
                                                        if (!newTest) {
                                                            createNewTest();
                                                            setShowTests(true);
                                                        } else {
                                                            alert("Please save or delete your existing test before creating a new one");
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <FocusDashboard focuses={userFocuses} summary={assessmentSummary} prevSummary={prevAssessmentSummary} sex={sex} demo={demo} />
                                    </div>
                                </div>
                            </div>
                            <div className="vpage-tests">
                                {showTests && (
                                    <div className="page-tests-scroll" ref={testsScrollRef}>
                                        <TestDetailsSection
                                            bloodTestFiles={bloodTestFiles}
                                            loadingFiles={loadingFiles}
                                            normalizeTestName={normalizeTestName}
                                            editTests={editTests}
                                            newTest={newTest}
                                            setNewTest={setNewTest}
                                            updateEditTest={updateEditTest}
                                            userFiles={userFiles}
                                            setUserFiles={setUserFiles}
                                            uploadingState={uploadingState}
                                            setUploadingState={setUploadingState}
                                            patientId={patientId}
                                            userId={user?.userId}
                                            selectedTest={selectedTest}
                                            onTestClick={handleTestClick}
                                            hideSystemFilter
                                            scrollToSystem={scrollToSystem}
                                            onDelete={() => setDeletePopup(true)}
                                            demo={demo}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="tab-panel panel-protocol" ref={protocolPanelRef}>
                        <div className="mobile-content">
                            <MobileProtocol
                                recommendations={recommendations}
                                recommendationMode={recommendationMode}
                                setRecommendationMode={setRecommendationMode}
                                loadingRecommendations={loadingRecommendations}
                                filteredTests={filteredTests}
                                selectedAlternatives={selectedAlternatives}
                                handleSelectAlternative={handleSelectAlternative}
                                handleGetRecommendations={handleGetRecommendations}
                                handleGetAllDefaults={handleGetAllDefaults}
                                highlightedRecommendations={highlightedRecommendations}
                                bloodTestFiles={bloodTestFiles}
                                selectedFile={selectedFile}
                                systemScoresArray={systemScoresArray}
                                testsForExport={testsForExport}
                                getFxRange={getFxRange}
                                savedProtocol={savedProtocol}
                                protocolLocked={protocolLocked}
                                effectiveLocked={effectiveLocked}
                                hasActiveDoctor={hasActiveDoctor}
                                lockedByName={lockedByName}
                                doctorViewMode={doctorViewMode}
                                demo={demo}
                                patientId={patientId}
                                userId={user?.userId}
                                canRegenerate={canRegenerate}
                                saveProtocol={saveProtocol}
                                refreshUserFiles={refreshUserFiles}
                                loadingFiles={loadingFiles}
                                userFocuses={userFocuses}
                                assessmentSummary={assessmentSummary}
                                sex={sex}
                            />
                        </div>
                    </div>
                    {showElisTab && (
                        <div className="tab-panel panel-elis" ref={elisPanelRef}>
                            {elisLocked ? (
                                <ElisLockedPanel trialUsed={elisTrialUsed} />
                            ) : (
                                <ChatDrawer
                                    patientId={patientId}
                                    patientName={patientName}
                                    isOpen={true}
                                    onToggle={() => handleTabChange('dashboard')}
                                    refreshUserFiles={refreshUserFiles}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
            {!showTests && mobileTab === 'dashboard' && !selectedSystem && bloodTestFiles?.length > 0 && (
                <button
                    className="tests-pill"
                    onClick={() => setShowTests(true)}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                        <rect x="9" y="3" width="6" height="4" rx="1" />
                        <path d="M9 12h6" />
                        <path d="M9 16h6" />
                    </svg>
                    <span>Tests</span>
                </button>
            )}
            {!readOnly && (
                <BottomNav
                    tabs={[
                        { id: 'dashboard', label: 'Dashboard', icon: (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7" rx="1" />
                                <rect x="14" y="3" width="7" height="7" rx="1" />
                                <rect x="3" y="14" width="7" height="7" rx="1" />
                                <rect x="14" y="14" width="7" height="7" rx="1" />
                            </svg>
                        )},
                        { id: 'protocol', label: 'Protocol', icon: (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                        )},
                        ...(showElisTab ? [{ id: 'elis', label: 'Elis', icon: (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                        )}] : []),
                    ]}
                    activeTab={mobileTab}
                    onTabChange={handleTabChange}
                    onUploadTest={handleFileInput}
                    onManualEntry={() => {
                        if (!newTest) {
                            createNewTest();
                            setShowTests(true);
                        } else {
                            alert("Please save or delete your existing test before creating a new one");
                        }
                    }}
                    uploadingState={uploadingState}
                    showFab={mobileTab !== 'elis'}
                    demoDisabled={demo}
                    pulseTabIds={demo ? ['protocol', 'elis'].filter(id => !visitedTabs.has(id)) : []}
                />
            )}
            {deletePopup && (
                <TestDeletePopup
                    filename={bloodTestFiles[selectedFile]?.key}
                    fileKey={bloodTestFiles[selectedFile]?.key}
                    fileKeys={bloodTestFiles[selectedFile]?.sourceKeys}
                    userFiles={userFiles}
                    setUserFiles={setUserFiles}
                    setSelectedFile={selectFile}
                    setDeletePopup={setDeletePopup}
                    patientId={patientId}
                    userId={user?.userId}
                />
            )}
        </div>
    );
}

export default MobileHome;
